/* eslint-disable @typescript-eslint/no-unused-vars */
/**
 * Event Writer Service API
 * Handles sending events to the Event Writer Service (EWS)
 */
import { EWS_API_BASE_URL } from '@/core/constants/appConfig/environment';
import type { TEventInputData } from '@/core/constants/event_types/additional_types';
import { ScriptHandler } from '@/core/handlers/script';
import { callAPI } from '@/shared/fetch_client';
import { Logger } from '@/shared/logger/logger';
import type { ShpyCpxHandlerInput } from '@/sources/bundles/wtag-shpfy-cpx/shopify_custom_pixel_types/cpx_payload_types';
import { getEwsWorker } from '@/workers/init_workers';

// Import only the necessary types, not the full module

/**
 * Base payload for all event types
 */
export type BaseEventPayload = {
  eventId: string;
  eventName: string;
  inputEventId?: string;
  inputEventName?: string;
  inputEventTimestamp?: string;
  orderId?: string;
  parsedParams?: string;
  sid?: string;
  sourceType: 'shopifyCustomPixel' | 'wtag';
  sourceUid: string;
  ssts?: string;
};

/**
 * Custom pixel event payload
 */
export type CustomPixelPayload = BaseEventPayload & {
  rawCpxPayload: ShpyCpxHandlerInput;
};

/**
 * WTAG event payload
 */
export type WtagEventPayload = BaseEventPayload & {
  wtagInput: TEventInputData;
};

/**
 * API Service for Event Writer Service
 */
const EventWriterServiceApi = {
  /**
   * Helper function to build query parameters
   * @param data - Base event data
   * @returns Record of query parameters
   */
  buildQueryParams(data: BaseEventPayload): Record<string, string> {
    const params: Record<string, string> = {
      event_id: data.eventId,
      event_name: data.eventName,
      input_event_id: data.inputEventId || '',
      input_event_name: data.inputEventName || '',
      input_event_timestamp: data.inputEventTimestamp || '',
      source_type: data.sourceType,
      source_uid: ScriptHandler.getInstance().getSourceUid(),
    };

    // Add optional params if they exist
    if (data.orderId) params['order_id'] = data.orderId;
    if (data.inputEventId) params['input_event_id'] = data.inputEventId;
    if (data.ssts) params['ssts'] = data.ssts;
    if (data.sid) params['sid'] = data.sid;

    return params;
  },

  /**
   * API route for the Event Writer Service
   */
  route: '/ews/generic-pixel',

  /**
   * Sends a custom pixel event payload to the Event Writer Service without blocking
   * @param data - The custom pixel event data
   * @returns A Promise that resolves to a boolean indicating success or failure
   */
  sendCustomPixelEvent(data: CustomPixelPayload): Promise<boolean> {
    // Create a non-blocking promise that will be resolved later
    return new Promise<boolean>(resolve => {
      // Use setTimeout with 0ms delay to move execution to the end of the event loop
      // This prevents blocking the main thread
      setTimeout(async () => {
        try {
          // Extract orderId from checkout data if available
          const orderId =
            data?.rawCpxPayload?.event?.data?.checkout?.order?.id || '';

          const params = this.buildQueryParams({
            ...data,
            orderId,
            sourceType: 'shopifyCustomPixel',
            sourceUid: ScriptHandler.getInstance().getSourceUid(),
          });

          // Get worker manager
          const workerManager = getEwsWorker();

          // If worker is available, use it to send the event
          if (workerManager?.isInitialized()) {
            Logger.debug('Using worker to send custom pixel event');

            // Create a trace ID based on event ID
            const traceId = `cpx-${data.eventId}`;

            // Use Logger.profileFnAsync instead of manual profiling
            void Logger.profileFnAsync(
              'submitTask:SEND_CUSTOM_PIXEL_EVENT',
              () =>
                workerManager.submitTask(
                  'SEND_CUSTOM_PIXEL_EVENT',
                  {
                    data: JSON.parse(JSON.stringify(data)),
                    params,
                    url: `${EWS_API_BASE_URL}${EventWriterServiceApi.route}`,
                  },
                  (error, _result) => {
                    if (error) {
                      Logger.error(
                        'Error in background SEND_CUSTOM_PIXEL_EVENT task',
                        error,
                      );
                    } else {
                      Logger.debug(
                        'Background SEND_CUSTOM_PIXEL_EVENT task completed',
                      );
                    }
                  },
                ),
              traceId,
            );

            // Resolve immediately since we're not waiting for the worker
            resolve(true);
          } else {
            // Fall back to direct API call
            Logger.debug(
              'Worker not available, using direct API call for custom pixel event',
            );

            // Create a trace ID based on event ID
            const traceId = `cpx-direct-${data.eventId}`;

            const response = await Logger.profileFnAsync(
              'directAPI:SEND_CUSTOM_PIXEL_EVENT',
              async () =>
                callAPI<{ ok: boolean; status: number }>({
                  body: JSON.stringify(data.rawCpxPayload),
                  method: 'POST',
                  params,
                  url: `${EWS_API_BASE_URL}${EventWriterServiceApi.route}`,
                }),
              traceId,
            );

            resolve(response.ok);
          }
        } catch (error) {
          Logger.error('Error sending custom pixel event to EWS', error);
          resolve(false);
        }
      }, 0);
    });
  },

  /**
   * Sends a WTAG event payload to the Event Writer Service without blocking
   * @param data - The WTAG event data
   * @returns A Promise that resolves to a boolean indicating success or failure
   */
  sendWtagEvent(data: WtagEventPayload): Promise<boolean> {
    // Create a non-blocking promise that will be resolved later
    return new Promise<boolean>(resolve => {
      // Use setTimeout with 0ms delay to move execution to the end of the event loop
      // This prevents blocking the main thread
      setTimeout(async () => {
        try {
          const params = this.buildQueryParams({
            ...data,
            sourceType: 'wtag',
            sourceUid: ScriptHandler.getInstance().getSourceUid(),
          });

          // Add eventID to eventData if it exists
          if (data.wtagInput?.eventData) {
            data.wtagInput.eventData.eventID = data.eventId;
          }

          // Get worker manager
          const workerManager = getEwsWorker();

          // If worker is available, use it to send the event
          if (workerManager?.isInitialized()) {
            Logger.debug('Using worker to send WTAG event');

            // Create a trace ID based on event ID
            const traceId = `wtag-${data.eventId}`;

            // Use Logger.profileFnAsync instead of manual profiling
            void Logger.profileFnAsync(
              'submitTask:SEND_WTAG_EVENT',
              () =>
                workerManager.submitTask(
                  'SEND_WTAG_EVENT',
                  {
                    data: JSON.parse(JSON.stringify(data)),
                    params,
                    url: `${EWS_API_BASE_URL}${EventWriterServiceApi.route}`,
                  },
                  (error, _result) => {
                    if (error) {
                      Logger.error(
                        'Error in background SEND_WTAG_EVENT task',
                        error,
                      );
                    } else {
                      Logger.debug('Background SEND_WTAG_EVENT task completed');
                    }
                  },
                ),
              traceId,
            );

            // Resolve immediately since we're not waiting for the worker
            resolve(true);
          } else {
            // Fall back to direct API call
            Logger.debug(
              'Worker not available, using direct API call for WTAG event',
            );

            // Create a trace ID based on event ID
            const traceId = `wtag-direct-${data.eventId}`;

            const response = await Logger.profileFnAsync(
              'directAPI:SEND_WTAG_EVENT',
              async () =>
                callAPI<{ ok: boolean; status: number }>({
                  body: JSON.stringify(data.wtagInput),
                  method: 'POST',
                  params,
                  url: `${EWS_API_BASE_URL}${EventWriterServiceApi.route}`,
                }),
              traceId,
            );

            resolve(response.ok);
          }
        } catch (error) {
          Logger.error('Error sending WTAG event to EWS', error);
          resolve(false);
        }
      }, 0);
    });
  },
};

export default EventWriterServiceApi;
